import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import { listHospitals } from '../services/hospitalService';
import {
  listNotOkCalculations,
  type NotOkCalculation,
  type NotOkCalculationsResponse,
} from '../services/adminService';
import type { Hospital } from '../types';

const ERROR_CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'Hospital_Share_Incorrect', label: 'Hospital Share Incorrect' },
  { value: 'AR/AP_Incorrect', label: 'AR/AP Incorrect' },
  { value: 'Parameter_Value_Incorrect', label: 'Parameter Value Incorrect' },
  { value: 'System_Variable_Incorrect', label: 'System Variable Incorrect' },
];

export default function NotOkReviewPage() {
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(true);

  // Filters
  const [hospitalUuid, setHospitalUuid] = useState('');
  const [errorCategory, setErrorCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');

  // Data
  const [data, setData] = useState<NotOkCalculationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Detail modal
  const [selectedCalc, setSelectedCalc] = useState<NotOkCalculation | null>(null);
  const [paramsCalc, setParamsCalc] = useState<NotOkCalculation | null>(null);
  const [remarksCalc, setRemarksCalc] = useState<NotOkCalculation | null>(null);

  useEffect(() => {
    listHospitals({ limit: 1000, active: 'true' })
      .then((res) => setHospitals(res.hospitals))
      .catch(() => {})
      .finally(() => setIsLoadingHospitals(false));
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await listNotOkCalculations({
        hospitalUuid: hospitalUuid || undefined,
        errorCategory: errorCategory || undefined,
        dateFrom: dateFrom || undefined,
        dateTill: dateTill || undefined,
        page,
        limit,
      });
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [hospitalUuid, errorCategory, dateFrom, dateTill, page]);

  useEffect(() => {
    if (!isLoadingHospitals) fetchData();
  }, [fetchData, isLoadingHospitals]);

  const handleFilter = () => {
    setPage(1);
    fetchData();
  };

  const handleClearFilters = () => {
    setHospitalUuid('');
    setErrorCategory('');
    setDateFrom('');
    setDateTill('');
    setPage(1);
  };

  const formatDate = (iso: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  };

  const totalPages = data?.pagination.totalPages ?? 1;

  if (isLoadingHospitals) {
    return (
      <div className="admin-review-page">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  return (
    <div className="admin-review-page">
      <div className="page-header">
        <h2>NOT_OK Calculations Review</h2>
      </div>

      {/* Filters */}
      <section className="review-filters">
        <Select
          label="Hospital"
          options={[
            { value: '', label: 'All Hospitals' },
            ...hospitals.map((h) => ({
              value: h.hospitalUuid,
              label: `${h.hospitalName} (${h.hospitalCode})`,
            })),
          ]}
          value={hospitalUuid}
          onChange={(e) => setHospitalUuid(e.target.value)}
        />
        <Select
          label="Error Category"
          options={ERROR_CATEGORIES}
          value={errorCategory}
          onChange={(e) => setErrorCategory(e.target.value)}
        />
        <Input
          label="Date From"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <Input
          label="Date Till"
          type="date"
          value={dateTill}
          onChange={(e) => setDateTill(e.target.value)}
        />
        <div className="filter-actions">
          <Button onClick={handleFilter}>Apply Filters</Button>
          <Button variant="secondary" onClick={handleClearFilters}>Clear</Button>
        </div>
      </section>

      {error && <Alert type="error">{error}</Alert>}

      {isLoading ? (
        <LoadingSpinner message="Loading calculations..." />
      ) : (
        <>
          <div className="review-summary">
            <span>Total: {data?.pagination.total ?? 0} flagged calculations</span>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ApptID</th>
                  <th>Hospital</th>
                  <th>Error Category</th>
                  <th>Remarks</th>
                  <th>Operator</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(!data || data.calculations.length === 0) ? (
                  <tr>
                    <td colSpan={7} className="table-empty-cell">
                      No NOT_OK calculations found.
                    </td>
                  </tr>
                ) : (
                  data.calculations.map((calc) => (
                    <tr key={calc.calculationId}>
                      <td>{calc.apptId}</td>
                      <td>{calc.hospitalName}</td>
                      <td>
                        <span className={`error-badge error-badge--${calc.errorCategory?.replace(/\//g, '-')}`}>
                          {calc.errorCategory}
                        </span>
                      </td>
                      <td>
                        <button
                          className="remarks-view-btn"
                          onClick={() => setRemarksCalc(calc)}
                          title="Click to view full remarks"
                        >
                          View
                        </button>
                      </td>
                      <td>{calc.operatorName}</td>
                      <td>{formatDate(calc.calculatedAt)}</td>
                      <td className="actions-cell">
                        <Button size="sm" onClick={() => setSelectedCalc(calc)}>
                          View Details
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/agreements/${calc.agreementId}/edit`)}
                        >
                          Agreement
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setParamsCalc(calc)}
                        >
                          Parameters
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav className="table-pagination" aria-label="Pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </nav>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedCalc}
        onClose={() => setSelectedCalc(null)}
        title="Calculation Details"
        size="lg"
      >
        {selectedCalc && (
          <div className="calc-detail">
            <div className="calc-detail-grid">
              <div className="calc-detail-item">
                <label>Calculation ID</label>
                <span>{selectedCalc.calculationId}</span>
              </div>
              <div className="calc-detail-item">
                <label>ApptID</label>
                <span>{selectedCalc.apptId}</span>
              </div>
              <div className="calc-detail-item">
                <label>Hospital</label>
                <span>{selectedCalc.hospitalName} ({selectedCalc.hospitalCode})</span>
              </div>
              <div className="calc-detail-item">
                <label>Agreement ID</label>
                <span>{selectedCalc.agreementId} (v{selectedCalc.agreementVersion})</span>
              </div>
              <div className="calc-detail-item">
                <label>Hospital Share</label>
                <span>{Number(selectedCalc.hospitalShare)?.toFixed(2)}</span>
              </div>
              <div className="calc-detail-item">
                <label>AR/AP</label>
                <span>{Number(selectedCalc.arAp)?.toFixed(2)}</span>
              </div>
              <div className="calc-detail-item">
                <label>Error Category</label>
                <span className="error-badge">{selectedCalc.errorCategory}</span>
              </div>
              <div className="calc-detail-item">
                <label>Operator</label>
                <span>{selectedCalc.operatorName} ({selectedCalc.operatorEmail})</span>
              </div>
              <div className="calc-detail-item">
                <label>Calculated At</label>
                <span>{formatDate(selectedCalc.calculatedAt)}</span>
              </div>
              <div className="calc-detail-item">
                <label>Execution Time</label>
                <span>{selectedCalc.executionTimeMs}ms</span>
              </div>
            </div>
            <div className="calc-detail-remarks">
              <label>Remarks</label>
              <p>{selectedCalc.remarks || 'No remarks provided.'}</p>
            </div>
          </div>
        )}
      </Modal>
      {/* Parameters Modal */}
      <Modal
        isOpen={!!paramsCalc}
        onClose={() => setParamsCalc(null)}
        title={`Parameters — ${paramsCalc?.apptId ?? ''}`}
        size="lg"
      >
        {paramsCalc && (
          <div className="calc-detail">
            {Object.keys(paramsCalc.inputParameterValues).length > 0 && (
              <>
                <h4>Input Parameters</h4>
                <table className="data-table">
                  <thead><tr><th>Parameter</th><th>Value</th></tr></thead>
                  <tbody>
                    {Object.entries(paramsCalc.inputParameterValues).map(([name, val]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td>{val != null ? String(val) : 'NA'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {Object.keys(paramsCalc.calculatedParameterValues).length > 0 && (
              <>
                <h4 style={{ marginTop: '1rem' }}>Calculated Parameters</h4>
                <table className="data-table">
                  <thead><tr><th>Parameter</th><th>Value</th></tr></thead>
                  <tbody>
                    {Object.entries(paramsCalc.calculatedParameterValues).map(([name, val]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td>{val != null ? String(val) : 'NA'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {Object.keys(paramsCalc.inputParameterValues).length === 0 &&
             Object.keys(paramsCalc.calculatedParameterValues).length === 0 && (
              <p>No parameter data available for this calculation.</p>
            )}
          </div>
        )}
      </Modal>

      {/* Remarks Modal */}
      <Modal
        isOpen={!!remarksCalc}
        onClose={() => setRemarksCalc(null)}
        title="Remarks"
        size="md"
      >
        {remarksCalc && (
          <div className="remarks-modal-content">
            <div className="remarks-modal-header">
              <div className="remarks-modal-info">
                <strong>ApptID:</strong> {remarksCalc.apptId}
              </div>
              <div className="remarks-modal-info">
                <strong>Hospital:</strong> {remarksCalc.hospitalName}
              </div>
              <div className="remarks-modal-info">
                <strong>Error Category:</strong>{' '}
                <span className="error-badge">{remarksCalc.errorCategory}</span>
              </div>
            </div>
            <div className="remarks-modal-body">
              <p>{remarksCalc.remarks || 'No remarks provided.'}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
